import { hasTemplate } from '../interpolation/hasTemplate';
import { interpolate } from '../interpolation/interpolate';

/**
 * Resolves `{{…}}` tokens in a step list, including object keys.
 *
 * Value interpolation already runs on plain props. Map keys do not: a pivot
 * `valueMap` uses the dimension value as the key, and that value is what the
 * host interpolates from form state or label tokens. A missing token becomes
 * an empty string.
 */
export function interpolateTransformSteps<T>(
  steps: readonly T[],
  context: unknown,
): T[] {
  return steps.map((step) => walk(step, context) as T);
}

function walk(value: unknown, context: unknown): unknown {
  if (typeof value === 'string') {
    if (!hasTemplate(value)) return value;
    return interpolate(value, context, { onMissing: 'empty' });
  }
  if (Array.isArray(value)) {
    return value.map((entry) => walk(entry, context));
  }
  if (value === null || typeof value !== 'object') return value;

  const out: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value)) {
    const resolvedKey = hasTemplate(key)
      ? stringifyKey(interpolate(key, context, { onMissing: 'empty' }))
      : key;
    out[resolvedKey] = walk(entry, context);
  }
  return out;
}

function stringifyKey(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value === undefined || value === null) return '';
  return String(value);
}
